import React, { useState, useEffect } from "react";
import { css } from "@emotion/css";
import { Button, Checkbox, Dropdown, Icon, Menu, Pagination, Table, Image } from "semantic-ui-react";
import { convertToMinFraction, dynamicSortMultiple } from "../../Helpers";
import "../../styles/table.scss";
import "../../styles/ui.scss";
import BCToolTip from "./BCToolTip";
import { getIsWhiteLabeled } from "../../helpers/PermissionHelper";

interface BCTableProps {
  data: any;
  dataRules?: any[];
  pagination?: number;
  sortable?: string[];
  fixed?: boolean;
  overflowX?: boolean;
  overflowY?: boolean;
  checkbox?: boolean;
  celled?: boolean;
  thin?: boolean;
  selectable?: boolean;
  dataSingleLine?: boolean;
  handleClick?: any;
  handleClickItem?: any;
  actions?: any;
  handleTableInvite?: any;
  handleTableDelete?: any;
  handleTableDeleteById?: any;
  handleTableEdit?: any;
  handleCheckbox?: any;
  handleCheckboxAll?: any;
  checkedItems?: any;
  unstackable?: boolean;
  isMobile?: boolean;
  brokerColor?: string | undefined;
  handleDrawRequest?: any;
  handleGMR?: any;
  handleCopyGMR?: any;
  handleSummary?: any;
  handleExport?: any;
  handleApplication?: any;
  handleCopyApplication?: any;
  ellipsis?: any;
  ellipsisHandlers? : any[];
  columns?: any;
  addRow?: any;
  addArray?: any [];
  handleTableAdd?: any;
  editMode?: any;
  isDeleteHide?: boolean;
  buttonWords?: any[];
  required?: boolean;
  tooltip?: any;
  multiline?: boolean;
  disableSummary?: boolean;
  disableApplication?: boolean;
  requiredFields?: string[];
  footer?: any;
  lastTableRow?: any;
}

const BCTable = ({ ...props }: BCTableProps) => {
  const [sortedHeaders, setSortedHeaders] = useState([] as string[]);
  const [sortedData, setSortedData] = useState(undefined as any);
  const [firstEntryIndex, setFirstEntryIndex] = useState(0);
  const [lastEntryIndex, setLastEntryIndex] = useState(0);
  const isBrokerColor = (props.brokerColor && getIsWhiteLabeled());

  const brokerTableHeaderOverrideCSS = css`
    background: ${props.brokerColor ? props.brokerColor : "black"}!important;
  `;

  const brokerTableEllipsesOverrideCSS = css`
    color: ${props.brokerColor ? props.brokerColor : "black"}!important;
  `;

  const handleTableMinHeight = (rows) => {
    return css`min-height: ${250 + (rows * 60)}px;`;
  };

  const handleHeaderClick = (header: string) => {
    sortedData.sort(dynamicSortMultiple(header));
    const index = sortedHeaders.indexOf(header);
    if (index !== -1) {
      sortedHeaders.splice(index, 1);
      sortedData.reverse();
    }
    else {
      sortedHeaders.push(header);
    }
    setSortedHeaders([...sortedHeaders]);
    setSortedData([...sortedData]);
  };

  const handlePageChange = (activePage: any) => {
    if (activePage > 0 && props.pagination && activePage <= Math.ceil(sortedData.length / props.pagination)) {
      setFirstEntryIndex(activePage * props.pagination - props.pagination);
      setLastEntryIndex(activePage * props.pagination);
    }
  };

  const handleHide = (prop: string) => {
    let show = true;
    if (props.dataRules && Array.from(props.dataRules, x => x.field).includes(prop)) {
      const dataRule = props.dataRules.find(x => x.field === prop);
      if (dataRule.rule === "hide") {
        show = false;
      }
    }
    return show;
  };

  const handleFormat = (item: string, prop: string) => {
    if (props.dataRules && Array.from(props.dataRules, x => x.field).includes(prop)) {
      const dataRule = props.dataRules.find(x => x.field === prop);
      if (item && dataRule.rule === "format" && dataRule.display === "dollar") {
        item = "$" + convertToMinFraction(item);
      }
    } 
    return item;
  };
  
  useEffect(() => {
    if (props.data) {
      setSortedData(props.data);
      if (firstEntryIndex === 0) {
        setLastEntryIndex(props.pagination ? props.pagination : props.data.length);
      }
    }

    if (props.sortable) {
      setSortedHeaders([...props.sortable]);
    }
  }, [props.pagination, props.data, props.sortable]);

  const PaginationButtons = () => {
    const menuItems = [] as any;
    if (props.pagination) {
      for (let i = 0; i < Math.ceil(sortedData.length / props.pagination); i++) {
        menuItems.push(<Menu.Item key={`pagination-button-${i}`} as='a' onClick={() => handlePageChange(i + 1)}>{i + 1}</Menu.Item>);
      }
    }
    return menuItems;
  };

  return (
    <React.Fragment>
      <div className={`bc-table-container ${handleTableMinHeight(sortedData && sortedData.length)} ${props.overflowX && "overflow-x-scroll"} ${props.overflowY && "overflow-y-scroll"}`}>
        {(sortedData && sortedData.length > 0) && <Table
          className={props.thin ? "bc-table-thin bc-table" : "bc-table"}
          celled={props.celled}
          selectable={props.selectable}
          fixed={props.fixed}
          unstackable={props.unstackable}
          columns={props.columns}
        >
          <Table.Header>
            <Table.Row>
              {props.checkbox && <Table.HeaderCell className={`${isBrokerColor ? brokerTableHeaderOverrideCSS : ""}`}>{props.handleCheckboxAll && 
                <Checkbox onChange={() => props.handleCheckboxAll()} checked={(props.checkedItems && props.data) && JSON.stringify(props.checkedItems) === JSON.stringify(props.data.map(x => x.id))} />}
              </Table.HeaderCell>}
              {Object.keys(sortedData[0]).map((item, i) => {
                const foundSortable = props.sortable ? props.sortable.find(x => x === item) : undefined;
                if (handleHide(item)) {
                  return (
                    <Table.HeaderCell
                      key={`table-header-${i}`}
                      className={`${isBrokerColor ? brokerTableHeaderOverrideCSS : ""} ${foundSortable ? "sortable-header" : ""}`}
                      onClick={() => foundSortable && handleHeaderClick(item)}
                      singleLine={props.dataSingleLine}
                    >
                      {item} {props.requiredFields?.includes(item) && <span className="required-asterix">*</span>}
                      <label className={"form-input-label"}>{(props.required && item === "Access Level") && <span className="required-asterix">*</span>}
                      {(props.tooltip && item === "Access Level") && <BCToolTip tooltip={props.tooltip} />}</label>
                      {(foundSortable && sortedHeaders.includes(item)) ? <Icon name='caret down' size="small" /> :
                        foundSortable ? <Icon name='caret up' size="small" /> : null}
                    </Table.HeaderCell>
                  );
                }
                else {
                  return null;
                }
              })}
              {props.actions && <Table.HeaderCell className={`${isBrokerColor ? brokerTableHeaderOverrideCSS : ""}`} textAlign='right'>
                {props.actions.includes("add") && <React.Fragment>
                  <Button 
                    className={`${isBrokerColor ? brokerTableHeaderOverrideCSS : ""}`}
                    icon
                    disabled={!props.editMode}
                    onClick={() => props.handleTableAdd()}
                  >
                    <div className="gmr-button-container">
                      <Icon name='add' className="bc-table-action-icon"/>
                    </div>
                  </Button>
                </React.Fragment>}
              </Table.HeaderCell>}
              {props.ellipsis && <Table.HeaderCell className={`${isBrokerColor ? brokerTableHeaderOverrideCSS : ""}`}/>}
            </Table.Row>
          </Table.Header>
          <Table.Body columns={props.data && props.data.length}>
            {sortedData.map((item: { [x: string]: any; statusLookupName: string; isEditHide: boolean; isDeleteHide: any; id: any; }, i: number) => {
              if (i >= firstEntryIndex && i < lastEntryIndex) {
                let checked = false;
                if (props.checkedItems) {
                  checked = props.checkedItems.includes(item.id);
                }

                let isSummary = true;
                let isApplication = true;
                if (props.disableSummary && ["New", "Sent"].indexOf(item.Status) > -1) {
                  isSummary = false;
                }
                
                if (props.disableApplication && ["Revision", "Submitted"].indexOf(item.Status) > -1) {
                  isApplication = false;
                }

                return (
                  <Table.Row key={`table-row-${i}`}>
                    {props.checkbox && <Table.Cell className="table-checkbox">
                      <Checkbox onChange={() => props.handleCheckbox && props.handleCheckbox(item.id)} checked={props.handleCheckbox && checked} key={`table-checkbox-${item.id}`}/>
                    </Table.Cell>}
                    {Object.keys(sortedData[0]).map((prop, j) => {
                      if (handleHide(prop)) {
                        return (
                          <Table.Cell 
                            key={`table-cell-${j}`} 
                            className={props.selectable ? "cursor-pointer" : ""} 
                            singleLine={props.multiline ? false : true}
                            onClick={() => props.handleClick ? props.handleClick(i) : 
                              props.handleClickItem ? props.handleClickItem(item) : null}
                          >
                            {handleFormat(item[prop], prop)}
                          </Table.Cell>
                        );
                      }
                      else {
                        return null;
                      }
                    })}
                    {props.actions && <Table.Cell>
                      {props.actions.includes("invite") && <Button 
                        className="table-button" 
                        icon
                        color="green"
                        inverted
                        onClick={() => props.handleTableInvite(i)}
                      >
                        <div className="gmr-button-container">
                          {props.isMobile ? null : <div className="table-button-text">{props.buttonWords && props.buttonWords.length > 0 ? props.buttonWords[i] : "Invite Now"}</div>}
                        </div>
                      </Button>}
                      {(props.actions.includes("edit") && item.isEditHide === false) && <Button 
                        className="table-button" 
                        icon
                        color="green"
                        inverted
                        onClick={() => props.handleTableEdit(i)}
                      >
                        <div className="gmr-button-container">
                          <Icon name='edit' />
                          {props.isMobile ? null : <div className="table-button-text">Edit</div>}
                        </div>
                      </Button>}
                      {(props.actions.includes("delete") && !props.isDeleteHide && !item.isDeleteHide) && <Button 
                        className="table-button" 
                        icon
                        color="red"
                        inverted
                        onClick={props.handleTableDeleteById ? () => props.handleTableDeleteById(item.id) : () => props.handleTableDelete(i)}
                      >
                        <div className="gmr-button-container">
                          <Icon name='trash' />
                          {props.isMobile ? null : <div className="table-button-text">Delete</div>}
                        </div>
                      </Button>}
                    </Table.Cell>}
                    {props.ellipsis && <Table.Cell textAlign='right'> 
                      <Image floated='right'>
                        <Dropdown key={"card-dropdown"} className={isBrokerColor ? brokerTableEllipsesOverrideCSS : "icon landing-page-card-header-icon"} icon="ellipsis horizontal" pointing='right'>
                          <Dropdown.Menu key={"card-menu"}>
                            {props.handleExport && <Dropdown.Item key={"export-quote"} onClick={() => props.handleExport(item.id)}>Export Quote</Dropdown.Item>}
                            {props.handleGMR && <Dropdown.Item key={"edit-gmr"} onClick={() => props.handleGMR(item)}>Edit Get My Rate</Dropdown.Item>}
                            {props.handleCopyGMR && <Dropdown.Item key={"copy-gmr"} onClick={() => props.handleCopyGMR(item)}>Copy Get My Rate</Dropdown.Item>}
                            {(props.handleApplication && isApplication) && <Dropdown.Item key={"edit-application"} onClick={() => props.handleApplication(item)}>Edit Application</Dropdown.Item>}
                            {props.handleCopyApplication && <Dropdown.Item key={"copy-application"} onClick={() => props.handleCopyApplication(item)}>Copy Application</Dropdown.Item>}
                            {(props.handleSummary && isSummary) && <Dropdown.Item key={"view-summary"} onClick={() => props.handleSummary(item.id)}>View Summary</Dropdown.Item>}
                            {props.ellipsisHandlers && props.ellipsisHandlers.map((ellipsisHandler, j) => {
                              return (
                                <Dropdown.Item key={`ellipsisHandler-${j}`} disabled={!props.editMode} onClick={() => ellipsisHandler.action(i, item)}>{ellipsisHandler.text}</Dropdown.Item>
                              );
                            })}
                            {props.handleDrawRequest && <React.Fragment> 
                              <Dropdown.Item key={"download-draw-management"} onClick={() => console.log("Download Draw Management")}>Download Draw Management</Dropdown.Item>
                              {item["Application Status"] && item["Application Status"] === "Pending" ? <React.Fragment>
                                <Dropdown.Item key={"resume"} onClick={() => console.log("Resume")}>Resume</Dropdown.Item>
                                <Dropdown.Item key={"delete"} onClick={() => console.log("Delete")}>Delete</Dropdown.Item>
                              </React.Fragment>
                              : <React.Fragment>
                                <Dropdown.Item key={"view"} onClick={() => console.log("View")}>View</Dropdown.Item>
                                <Dropdown.Item key={"download-inspection-report"} onClick={() => console.log("Download Inspection Report")}>Download Inspection Report</Dropdown.Item>
                              </React.Fragment>}
                            </React.Fragment>}
                          </Dropdown.Menu>
                        </Dropdown>
                        </Image>
                    </Table.Cell>}
                  </Table.Row>
                );
              }
              else {
                return null;
              }
            })}
            {props.lastTableRow && <Table.Row fullWidth>
              <Table.Cell colSpan='16'>{props.lastTableRow}</Table.Cell>
            </Table.Row>}
          </Table.Body>
        </Table>
        }
      </div>
      {(props.pagination && sortedData && props.pagination < sortedData.length) && <Table.Footer>
        <Table.Row>
          <Table.HeaderCell colSpan='16'>
            {props.pagination > 8 ? <Pagination 
              pointing
              secondary
              defaultActivePage={1}
              totalPages={Math.ceil(sortedData.length / props.pagination)}
              onPageChange={(e, data) => handlePageChange(data.activePage)}
            /> :
              <Menu pagination>
                <Menu.Item as='a' icon onClick={() => props.pagination && handlePageChange(lastEntryIndex / props.pagination - 1)}>
                  <Icon name='chevron left' />
                </Menu.Item>
                <PaginationButtons />
                <Menu.Item as='a' icon onClick={() => props.pagination && handlePageChange(lastEntryIndex / props.pagination + 1)}>
                  <Icon name='chevron right' />
                </Menu.Item>
              </Menu>}
          </Table.HeaderCell>
        </Table.Row>
      </Table.Footer>}
    </React.Fragment>
  );
};

export default BCTable;